import { ClipboardIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';

import * as api from '../../services/integration-api.js';

import { useConnectorScope } from './connector-scope.js';

/**
 * Databricks connects with a **service principal**, not OAuth: a client id and secret created in the
 * customer's Databricks account and granted access to the workspace.
 *
 * There is deliberately **no tenant or directory field**, unlike the Dynamics 365 form next door.
 * That one asks for an Entra tenant because Dataverse uses an Entra service principal; this
 * credential is Databricks-native and never touches Entra, despite the workspace being Azure
 * Databricks. A form asking for a tenant here would be collecting the wrong credential type
 * entirely, so its absence is load-bearing rather than an oversight.
 *
 * Four fields, and the workspace URL matters most: it is stored on the connection rather than
 * configured per deployment, so one workspace can point at one customer's Databricks while another
 * points somewhere else.
 */

interface FormErrors {
  workspaceUrl?: string;
  clientId?: string;
  clientSecret?: string;
  warehouseId?: string;
  submit?: string;
}

/**
 * Client-side mirror of identity's `workspace_url` allowlist: only the `adb-` workspace host form.
 *
 * This is not fussiness. A vanity alias such as `<org>.azuredatabricks.net` is a Traffic Manager
 * front door that answers API calls with HTTP 200 and an HTML sign-in page — so a connection saved
 * against it looks healthy and returns nonsense at query time. Catching it on the form is the
 * difference between a clear message here and a mystery later.
 *
 * The server re-validates and its message is surfaced verbatim on submit; this only exists to fail
 * fast before a round-trip.
 */
const DATABRICKS_HOST = /^adb-\d+\.\d+\.azuredatabricks\.net$/;

function isDatabricksWorkspaceUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === 'https:' &&
      // A port or userinfo is not part of a workspace host. The server strips both when it
      // canonicalizes, so accepting them here would show the user a value that is not what gets
      // stored.
      parsed.port === '' &&
      parsed.username === '' &&
      parsed.password === '' &&
      DATABRICKS_HOST.test(parsed.hostname.toLowerCase())
    );
  } catch {
    return false;
  }
}

/**
 * The origin alone, matching what identity stores. People copy the URL out of the Databricks UI, so
 * a path and query (`/sql/warehouses?o=…`) is the common case. Canonicalizing here means the value
 * the user sees accepted is the value that ends up on the connection.
 */
function toWorkspaceOrigin(value: string): string {
  try {
    return `https://${new URL(value).hostname.toLowerCase()}`;
  } catch {
    return value;
  }
}

function validate(values: api.DatabricksCredentialsPayload): FormErrors {
  const errors: FormErrors = {};
  if (!values.workspaceUrl) {
    errors.workspaceUrl = 'Workspace URL is required';
  } else if (!isDatabricksWorkspaceUrl(values.workspaceUrl)) {
    errors.workspaceUrl =
      'Enter the workspace URL, e.g. https://adb-1234567890123456.7.azuredatabricks.net';
  }
  if (!values.clientId) errors.clientId = 'Client ID is required';
  if (!values.clientSecret) {
    errors.clientSecret = 'Client secret is required';
  } else if (values.clientSecret.length < 8) {
    errors.clientSecret = 'Client secret looks too short';
  }
  if (!values.warehouseId) errors.warehouseId = 'SQL warehouse ID is required';
  return errors;
}

type TextField = 'workspaceUrl' | 'clientId' | 'warehouseId';

export function DatabricksCredentialsForm({
  onSaved,
  onCancel,
  details,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
}): JSX.Element {
  const [values, setValues] = useState<api.DatabricksCredentialsPayload>(
    () => ({
      clientId: '',
      clientSecret: '',
      workspaceUrl: '',
      warehouseId: '',
    }),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();

  const handleChange =
    (field: TextField | 'clientSecret') =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = e.target;
      setValues((v) => ({ ...v, [field]: value }));
      if (errors[field]) setErrors((es) => ({ ...es, [field]: undefined }));
    };

  /** Reads the clipboard into the secret field on an explicit user gesture. */
  const handlePasteFromClipboard = async (): Promise<void> => {
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setValues((v) => ({ ...v, clientSecret: text.trim() }));
      if (errors.clientSecret)
        setErrors((es) => ({ ...es, clientSecret: undefined }));
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const trimmed: api.DatabricksCredentialsPayload = {
      workspaceUrl: values.workspaceUrl.trim(),
      clientId: values.clientId.trim(),
      clientSecret: values.clientSecret.trim(),
      warehouseId: values.warehouseId.trim(),
    };
    const found = validate(trimmed);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    /**
     * Canonicalize AFTER validating, never before. `toWorkspaceOrigin` rebuilds the URL as
     * `https://<host>`, so running it first would silently upgrade an `http://` value and defeat the
     * protocol check the validator just made.
     */
    trimmed.workspaceUrl = toWorkspaceOrigin(trimmed.workspaceUrl);
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveDatabricksCredentials(trimmed, workspaceId, details);
      onSaved();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      {/*
        Two things a first-time connector cannot guess: this connection is theirs and reaches nobody
        else, so connecting it is not a favour to the team but a step each person takes; and the
        credential is not something they can create themselves — a Databricks account admin registers
        the service principal and grants it access to the workspace and the data. Saying the second
        without the first is what leaves somebody waiting for a setup that will never arrive.
      */}
      <p className="text-xs text-muted-foreground">
        This connection is yours — nobody else here uses it. You cannot create
        the credential yourself, so ask a Databricks account administrator for
        one: they add a service principal under{' '}
        <span className="font-medium">
          Settings → Identity and access → Service principals
        </span>
        , generate an OAuth secret, then grant it access to the workspace and
        the catalogs it should read.
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="databricks-workspace-url" className="text-xs">
          Workspace URL
        </Label>
        <Input
          id="databricks-workspace-url"
          type="url"
          autoComplete="url"
          spellCheck={false}
          placeholder="https://adb-1234567890123456.7.azuredatabricks.net"
          value={values.workspaceUrl}
          onChange={handleChange('workspaceUrl')}
          disabled={submitting}
          aria-invalid={Boolean(errors.workspaceUrl)}
          aria-describedby={
            errors.workspaceUrl
              ? 'databricks-workspace-url-err'
              : 'databricks-workspace-url-help'
          }
        />
        {errors.workspaceUrl ? (
          <p
            id="databricks-workspace-url-err"
            className="text-xs text-destructive"
          >
            {errors.workspaceUrl}
          </p>
        ) : (
          <p
            id="databricks-workspace-url-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Copy it from the browser address bar in Databricks — it starts with{' '}
            <span className="font-medium">adb-</span>. A shortcut like
            company.azuredatabricks.net will not work.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="databricks-client-id" className="text-xs">
          Client ID
        </Label>
        <Input
          id="databricks-client-id"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="00000000-0000-0000-0000-000000000000"
          value={values.clientId}
          onChange={handleChange('clientId')}
          disabled={submitting}
          aria-invalid={Boolean(errors.clientId)}
          aria-describedby={
            errors.clientId
              ? 'databricks-client-id-err'
              : 'databricks-client-id-help'
          }
        />
        {errors.clientId ? (
          <p id="databricks-client-id-err" className="text-xs text-destructive">
            {errors.clientId}
          </p>
        ) : (
          <p
            id="databricks-client-id-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Shown on the service principal in Databricks. This is not a
            Microsoft Entra app — Databricks issues its own.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="databricks-client-secret" className="text-xs">
          Client secret
        </Label>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="databricks-client-secret"
              type={showSecret ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="Client secret value"
              value={values.clientSecret}
              onChange={handleChange('clientSecret')}
              disabled={submitting}
              aria-invalid={Boolean(errors.clientSecret)}
              aria-describedby={
                errors.clientSecret
                  ? 'databricks-client-secret-err'
                  : 'databricks-client-secret-help'
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowSecret((s) => !s)}
              aria-label={showSecret ? 'Hide secret' : 'Show secret'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showSecret ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            disabled={submitting}
            aria-label="Paste secret from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteError && (
          <p className="text-[11px] text-destructive">{pasteError}</p>
        )}
        {errors.clientSecret ? (
          <p
            id="databricks-client-secret-err"
            className="text-xs text-destructive"
          >
            {errors.clientSecret}
          </p>
        ) : (
          <p
            id="databricks-client-secret-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Databricks shows the secret once, when the service principal is
            created.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="databricks-warehouse-id" className="text-xs">
          SQL warehouse ID
        </Label>
        <Input
          id="databricks-warehouse-id"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="445e0c003a951d83"
          value={values.warehouseId}
          onChange={handleChange('warehouseId')}
          disabled={submitting}
          aria-invalid={Boolean(errors.warehouseId)}
          aria-describedby={
            errors.warehouseId
              ? 'databricks-warehouse-id-err'
              : 'databricks-warehouse-id-help'
          }
        />
        {errors.warehouseId ? (
          <p
            id="databricks-warehouse-id-err"
            className="text-xs text-destructive"
          >
            {errors.warehouseId}
          </p>
        ) : (
          <p
            id="databricks-warehouse-id-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            The warehouse queries run on. Open{' '}
            <span className="font-medium">SQL Warehouses</span> in Databricks
            and copy the ID from the one you want — a serverless warehouse
            starts in seconds.
          </p>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
